import type { TypedSupabaseClient } from '../client';
import type {
  IResourcesRepository,
  ResourceFolder,
  CreateResourceFolderInput,
  UpdateResourceFolderInput,
  ResourceFile,
  CreateResourceFileInput,
  UpdateResourceFileInput,
  ResourceFileFilters,
} from '../../../interfaces/src';

// resource_folders / resource_files n'apparaissent pas encore dans Database types —
// on casse la contrainte le temps que `supabase gen types` soit relancé.
const anyFrom = (client: TypedSupabaseClient) =>
  (table: string) => (client as unknown as { from: (t: string) => any }).from(table);

export class SupabaseResourcesRepository implements IResourcesRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  // ---------- Folders ----------

  async listFolders(): Promise<ResourceFolder[]> {
    const { data, error } = await anyFrom(this.supabase)('resource_folders')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapFolder);
  }

  async createFolder(input: CreateResourceFolderInput): Promise<ResourceFolder> {
    const { data, error } = await anyFrom(this.supabase)('resource_folders')
      .insert({
        name: input.name,
        parent_id: input.parentId ?? null,
        color: input.color ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapFolder(data);
  }

  async updateFolder(id: string, input: UpdateResourceFolderInput): Promise<ResourceFolder> {
    const patch: Record<string, unknown> = {};
    if (input.name !== undefined) patch['name'] = input.name;
    if (input.parentId !== undefined) patch['parent_id'] = input.parentId;
    if (input.color !== undefined) patch['color'] = input.color;

    const { data, error } = await anyFrom(this.supabase)('resource_folders')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapFolder(data);
  }

  async deleteFolder(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('resource_folders').delete().eq('id', id);
    if (error) throw error;
  }

  // ---------- Files (metadata only) ----------

  async listFiles(filters?: ResourceFileFilters): Promise<ResourceFile[]> {
    let q = anyFrom(this.supabase)('resource_files').select('*');
    if (filters?.folderId === null) q = q.is('folder_id', null);
    else if (filters?.folderId !== undefined) q = q.eq('folder_id', filters.folderId);
    if (filters?.search && filters.search.trim()) {
      q = q.ilike('name', `%${filters.search.trim()}%`);
    }
    q = q.order('name', { ascending: true });
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(mapFile);
  }

  async createFile(input: CreateResourceFileInput): Promise<ResourceFile> {
    const { data, error } = await anyFrom(this.supabase)('resource_files')
      .insert({
        folder_id: input.folderId,
        name: input.name,
        storage_path: input.storagePath,
        mime_type: input.mimeType,
        size_bytes: input.sizeBytes,
      })
      .select()
      .single();
    if (error) throw error;
    return mapFile(data);
  }

  async updateFile(id: string, input: UpdateResourceFileInput): Promise<ResourceFile> {
    const patch: Record<string, unknown> = {};
    if (input.name !== undefined) patch['name'] = input.name;
    if (input.folderId !== undefined) patch['folder_id'] = input.folderId;

    const { data, error } = await anyFrom(this.supabase)('resource_files')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapFile(data);
  }

  async deleteFile(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('resource_files').delete().eq('id', id);
    if (error) throw error;
  }
}

function mapFolder(row: Record<string, unknown>): ResourceFolder {
  return {
    id: row['id'] as string,
    parentId: (row['parent_id'] as string | null) ?? null,
    name: row['name'] as string,
    color: (row['color'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapFile(row: Record<string, unknown>): ResourceFile {
  return {
    id: row['id'] as string,
    folderId: (row['folder_id'] as string | null) ?? null,
    name: row['name'] as string,
    storagePath: row['storage_path'] as string,
    mimeType: (row['mime_type'] as string | null) ?? null,
    sizeBytes: Number(row['size_bytes'] ?? 0),
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
